import { createFeature, createReducer, on } from '@ngrx/store';
import {
  FAILED_FLAG_EXPIRY_BUFFER_IN_MS,
  FLAG_EXPIRY_BUFFER_IN_MS,
} from 'src/app/core/constants/feature-flag.constants';
import { NgRxActionProps } from 'src/app/core/state/models/ngrx-action-props.model';
import { FeatureFlagActions } from 'src/app/core/state/slices/feature-flag/feature-flag.actions';
import {
  FeatureFlag,
  FeatureFlagFetching,
  FeatureFlagState,
  FetchFeatureFlagProps,
  SetFailedFeatureFlagProps,
  SetFeatureFlagProps,
} from 'src/app/core/state/slices/feature-flag/feature-flag.model';
import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';

// NgRx Entity adapter for FeatureFlag
export const featureFlagsAdapter: EntityAdapter<FeatureFlag> = createEntityAdapter<FeatureFlag>({});

// NgRx Entity adapter for FeatureFlagFetching
export const featureFlagsFetchingAdapter: EntityAdapter<FeatureFlagFetching> = createEntityAdapter<FeatureFlagFetching>(
  {},
);

// Initial state of the feature
export const initialState: FeatureFlagState = {
  featureFlags: featureFlagsAdapter.getInitialState({}),
  featureFlagsFetching: featureFlagsFetchingAdapter.getInitialState({}),
};

/**
 * Process the feature flag response from the backend and set its required values, like expiry, before
 * saving it to the Store.
 *
 * @param fetchedFlags - The response object from the backend.
 * @param attributes - The attributes used for fetching the feature flags.
 */

const prepareFlagsToStore = (fetchedFlags: Record<string, boolean>, attributes: any): FeatureFlag[] =>
  Object.keys(fetchedFlags).map((featureKey) => ({
    id: featureKey,
    enabled: fetchedFlags[featureKey],
    attributes,
    expiry: Date.now() + FLAG_EXPIRY_BUFFER_IN_MS,
  }));

/**
 * Process the feature flag response from a failed backend request and set its enabled to FALSE before saving it to the Store.
 *
 * @param featureFlagState - The feature flag Store state.
 * @param featureKeys - The feature flag keys to process.
 * @param attributes - The attributes used for fetching the feature flags.
 */

const prepareFailedFlagsToStore = (featureKeys: string[], attributes: any): FeatureFlag[] =>
  featureKeys.map((featureKey: string) => ({
    id: featureKey,
    enabled: false, // Setting it to FALSE as default for failed response.
    attributes,
    expiry: Date.now() + FAILED_FLAG_EXPIRY_BUFFER_IN_MS, // Setting the expiry so it will be fetched again.
  }));

/**
 * Reducers are pure functions that must always return the new state object and not mutate it.
 */
const featureFlagReducer = createReducer<FeatureFlagState>(
  initialState,
  on(FeatureFlagActions.resetState, () => ({ ...initialState })),
  on(FeatureFlagActions.setFeatureFlags, (state, featureFlagsActionProps: NgRxActionProps<SetFeatureFlagProps>) => ({
    ...state,
    // Upsert is here to create or overwrite existing entries in store.
    featureFlags: featureFlagsAdapter.upsertMany(
      prepareFlagsToStore(featureFlagsActionProps.featureFlags, featureFlagsActionProps.attributes),
      state.featureFlags,
    ),
    // Removing hash of the fetching features.
    featureFlagsFetching: featureFlagsFetchingAdapter.removeOne(
      featureFlagsActionProps.featureFlagKeysHash,
      state.featureFlagsFetching,
    ),
  })),

  // Insert a new set of feature flag as enabled FALSE from a failed fetch request. This will not override existing flags in the store.
  on(
    FeatureFlagActions.setFailedFeatureFlags,
    (state, failedFeatureFlagsActionProps: NgRxActionProps<SetFailedFeatureFlagProps>) => ({
      ...state,
      // AddMany does not overwrite existing entries, it is there just to add new ones.
      featureFlags: featureFlagsAdapter.addMany(
        prepareFailedFlagsToStore(failedFeatureFlagsActionProps.featureKeys, failedFeatureFlagsActionProps.attributes),
        state.featureFlags,
      ),
      // Removing hash of the fetching features.
      featureFlagsFetching: featureFlagsFetchingAdapter.removeOne(
        failedFeatureFlagsActionProps.featureFlagKeysHash,
        state.featureFlagsFetching,
      ),
    }),
  ),

  on(
    FeatureFlagActions.fetchFeatureFlags,
    (state, fetchFeatureFlagActionProps: NgRxActionProps<FetchFeatureFlagProps>) => ({
      ...state,
      featureFlagsFetching: featureFlagsFetchingAdapter.addOne(
        {
          id: fetchFeatureFlagActionProps.featureFlagKeysHash,
          featureFlagKeys: fetchFeatureFlagActionProps.featureKeys,
        },
        state.featureFlagsFetching,
      ),
    }),
  ),
);

/**
 * Creates feature that can be used as feature slice in StoreModule.forFeature.
 * Also creates feature selector and a select for each feature state property.
 */
export const featureFlagFeature = createFeature({
  name: 'FeatureFlagFeature',
  reducer: featureFlagReducer,
});
